// src/components/HeaderSearch/HeaderSearch.jsx
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'; // Для React Router v6
import api from '../../api';
import SearchForm from '../SearchForm';
import { SearchContext } from '../../context/SearchContext';
import GeolocationButton from '../GeolocationButton';
import fetchLocationByIP from '../../utils/fetchLocationByIP';
import MultiSelectWithDependentCities from '../MultiSelects/MultiSelectWithDependentCities';
import MultiSelectInColumnWithLabel from '../MultiSelects/MultiSelectInColumnWithLabel';
import MultiSelectFoundationYear from '../MultiSelects/MultiSelectFoundationYear';
import StarsDemo from '../../utils/animateWhiteStar';
import { TextAnimator } from '../../utils/text-animator';
import SpecializationTabs from './SpecializationTabs';
import { Button, Menu, Box } from '@mui/material';
import AskMikeMenu from './AskMikeMenu';

const HeaderSearch = ({ minimizeBanner, minimizeHeader, region }) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        setSearchResults,
        changeSearchSended,
        suggestion,
        showAdvanced,
        setShowAdvanced
    } = useContext(SearchContext);

    // ... (all other state and useEffect hooks remain the same) ...
    const [selectedLanguage, setSelectedLanguage] = useState([]);
    const [selectedState, setSelectedState] = useState([]);
    const [selectedCity, setSelectedCity] = useState([]);
    const [selectedCityName, setSelectedCityName] = useState('');
    const [selectedStateName, setSelectedStateName] = useState('');
    const [defaultCity, setDefaultCity] = useState(null);
    const [defaultCityName, setDefaultCityName] = useState(null);
    const [defaultStateName, setDefaultStateName] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [zipCode, setZipCode] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [isConsultationCheck, setIsConsultationCheck] = useState(false);
    const [selectedFoundationYears, setSelectedFoundationYears] = useState([]);
    const [showNotification, setShowNotification] = useState(false);
    const textRef = useRef(null);
    const [animator, setAnimator] = useState(null);
    const ALL_SPECIALIZATION = { id: null, name: 'All' };
    const [selectedSpecialization, setSelectedSpecialization] = useState(ALL_SPECIALIZATION);
    const [isAllSpecializationActive, setIsAllSpecializationActive] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [initialProblemFromUrl, setInitialProblemFromUrl] = useState('');

    useEffect(() => {
        const problemParam = searchParams.get('problem');
        if (problemParam) {
            console.log("HeaderSearch: Найден параметр 'problem' в URL:", problemParam);
            setInitialProblemFromUrl(problemParam);
        }
    }, [searchParams]);

    useEffect(() => {
        const fetchLanguages = async () => {
             try {
                 if (api && typeof api.get === 'function') {
                    const response = await api.get('/languages/', { withoutAuth: true });
                    const uniqueLanguages = Array.from(new Set(response.data.map(lang => lang.name)))
                        .map(name => response.data.find(lang => lang.name === name));
                    setLanguages(uniqueLanguages);
                 } else {
                     console.error("api.get is not a function or api is undefined");
                 }
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };
        fetchLanguages();
    }, []);

    useEffect(() => {
        const getCityFromIP = async () => {
            if (typeof fetchLocationByIP === 'function') {
                const locationObj = await fetchLocationByIP();
                if (locationObj && locationObj.city && locationObj.region) {
                    setDefaultCityName(locationObj.city);
                    setDefaultStateName(locationObj.region);
                    setSelectedCityName(locationObj.city);
                    setSelectedStateName(locationObj.region);
                    setSelectedCity([{ name: locationObj.city, city_id: null }]);
                }
            } else {
                console.warn("fetchLocationByIP is not defined or not a function.");
            }
        };
        if (!userLocation && !zipCode) {
            getCityFromIP();
        }
    }, [userLocation, zipCode]);

    useEffect(() => {
        if (textRef.current && typeof TextAnimator === 'function') {
             try {
                const animatorInstance = new TextAnimator(textRef.current);
                setAnimator(animatorInstance);
                animatorInstance.animate();
             } catch(e) {
                 console.error("Error initializing TextAnimator:", e);
             }
        }
    }, [suggestion]);

    const toggleAdvancedSearch = () => {
        setShowAdvanced(!showAdvanced);
        if (minimizeBanner) minimizeBanner();
        if (minimizeHeader) minimizeHeader();
    };

    const handleSearch = (data) => {
        console.log("HeaderSearch: handleSearch получил данные от SearchForm:", data);
        setShowNotification(false);
        setTimeout(() => {
            if (setSearchResults) setSearchResults(data); else console.warn("setSearchResults is not available in context");
            if (changeSearchSended) changeSearchSended(); else console.warn("changeSearchSended is not available in context");
            if (setShowAdvanced) setShowAdvanced(false); else console.warn("setShowAdvanced is not available in context");

            if (minimizeBanner) minimizeBanner();
            if (minimizeHeader) minimizeHeader();
            setShowNotification(true);

            // +++ ИЗМЕНЕНА ПРОВЕРКА ПУТИ +++
            // Check if the current path STARTS WITH /searching-request
            // The route is /searching-request/:resultToken
            if (location.pathname.startsWith('/searching-request')) {
                console.log(`HeaderSearch: Current pathname ('${location.pathname}') starts with /searching-request. Navigating to /ai.`);
                navigate('/ai'); // Navigate to /ai page
            } else {
                console.log(`HeaderSearch: Navigation condition not met. Current pathname: '${location.pathname}'.`);
            }
            // +++ КОНЕЦ ИЗМЕНЕНИЯ +++
        }, 100);
    };

    const handleCloseNotification = () => {
        setShowNotification(false);
    };

    const handleLocationObtained = (zip, locationData) => {
        setUserLocation(locationData);
        setZipCode(zip);
        if (minimizeBanner) minimizeBanner();
        if (minimizeHeader) minimizeHeader();
    };

    const handleSpecializationClick = (spec) => {
        setSelectedSpecialization(spec);
        setIsAllSpecializationActive(spec.name === 'All');
        if (minimizeBanner) minimizeBanner();
        if (minimizeHeader) minimizeHeader();
    };

    const handleSpecializationChange = (spec) => {
        setSelectedSpecialization(spec);
        setIsAllSpecializationActive(spec.name === 'All');
        if (minimizeBanner) minimizeBanner();
        if (minimizeHeader) minimizeHeader();
    };

    const handleConsultationClick = useCallback(() => {
        setIsConsultationCheck(!isConsultationCheck);
    }, [isConsultationCheck]);

    const onCityStateChange = (cities, states) => {
        setSelectedCity(cities);
        setSelectedState(states);
        if (cities.length > 0) {
            setSelectedCityName(cities[0].name);
        } else {
             setSelectedCityName('');
        }
        if (states.length > 0) {
            setSelectedStateName(states[0].name);
        } else {
             setSelectedStateName('');
        }
    };

     const handleMUIButtonClick = (event) => setAnchorEl(event.currentTarget);
     const handleMenuClose = () => setAnchorEl(null);

    useEffect(() => {
        if (suggestion) {
            setShowNotification(true);
        }
    }, [suggestion]);


    return (
        <div className="header__search" style={{ position: 'relative' }}>
            <div className="container">
                <div className="content">
                    {showNotification && (
                        <div className="search__notification" onClick={handleCloseNotification}>
                            {typeof StarsDemo === 'function' && <StarsDemo />}
                            <p ref={textRef}>{suggestion}</p>
                            <button>
                                <img src="assets/icon--cross.svg" alt="Close Icon" />
                            </button>
                        </div>
                    )}
                    <SearchForm
                        initialProblem={initialProblemFromUrl}
                        onSearch={handleSearch}
                        cities={[...(defaultCity ? [defaultCity] : []), ...selectedCity]}
                        cityName={defaultCityName}
                        stateName={defaultStateName}
                        selectedCityName={selectedCityName}
                        selectedStateName={selectedStateName}
                        states={selectedState}
                        zipCode={zipCode}
                        specialization={selectedSpecialization}
                        onSpecializationChange={handleSpecializationChange}
                        languages={selectedLanguage}
                        isAllSpecializationActive={isAllSpecializationActive}
                        freeConsultation={isConsultationCheck}
                        foundationYears={selectedFoundationYears}
                        minimizeHeader={minimizeHeader}
                        minimizeBanner={minimizeBanner}
                    />
                    {/* ... остальной JSX без изменений ... */}
                    <div className="search__filters">
                         <div className="tabs-scroll-wrap">
                             {typeof SpecializationTabs === 'function' && <SpecializationTabs
                                selectedSpecialization={selectedSpecialization}
                                handleSpecializationClick={handleSpecializationClick}
                                isAllSpecializationActive={isAllSpecializationActive}
                            />}
                        </div>
                        <div className="search__side">
                            {typeof AskMikeMenu === 'function' && <AskMikeMenu
                                minimizeBanner={minimizeBanner}
                                minimizeHeader={minimizeHeader}
                                region={region}
                            />}
                            <button
                                className={"search__adv" + (showAdvanced ? ' open' : '')}
                                onClick={toggleAdvancedSearch}
                            >
                                Advanced Search
                            </button>
                             {typeof Menu === 'function' && Button && Box && <Menu> {/* ...content... */} </Menu>}
                            {typeof GeolocationButton === 'function' && <GeolocationButton onLocationObtained={handleLocationObtained} />}
                        </div>
                    </div>
                     <div className={`search__advanced ${showAdvanced ? 'show' : ''}`}>
                         <div className="grid-container">
                             {typeof MultiSelectWithDependentCities === 'function' && <MultiSelectWithDependentCities
                                 label="City"
                                 onChange={onCityStateChange}
                                 states={selectedState}
                                 placeholder="Select city"
                             />}
                             {typeof MultiSelectInColumnWithLabel === 'function' && <MultiSelectInColumnWithLabel
                                 label="Language"
                                 options={languages.map(lang => ({ name: lang.name, code: lang.name }))}
                                 onChange={setSelectedLanguage}
                                 placeholder=""
                             />}
                             {typeof MultiSelectFoundationYear === 'function' && <MultiSelectFoundationYear
                                 label="Foundation Year"
                                 onChange={setSelectedFoundationYears}
                             />}
                         </div>
                         <div className="header__search__buttons">
                              <div className="column desktop">
                                 <label className="label">Consultations</label>
                                 <label
                                     onClick={handleConsultationClick}
                                     className={isConsultationCheck ? 'consultations-checkbox checked' : 'consultations-checkbox'}
                                 >
                                     <span>All</span>
                                     <span>Free</span>
                                 </label>
                             </div>
                              <div className="column mobile">
                                 <label className="label">&nbsp;</label>
                                 <div className="search__radius">
                                     <span>Free Consultation</span>
                                     <label
                                         onClick={handleConsultationClick}
                                         className={isConsultationCheck ? 'toggle checked' : 'toggle'}
                                     >
                                         <div className="toggle__container">
                                             <div className="toggle__circle"></div>
                                         </div>
                                     </label>
                                 </div>
                             </div>
                         </div>
                     </div>
                </div>
            </div>
        </div>
    );
};

export default HeaderSearch;